import { createFileRoute } from "@tanstack/react-router";
import { LegalPage, LegalSection } from "@/components/legal-page";

export const Route = createFileRoute("/privacidade")({
  head: () => ({
    meta: [
      { title: "Política de Privacidade | H2J Growth Company" },
      {
        name: "description",
        content:
          "Saiba como a H2J Growth Company coleta, utiliza e protege seus dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD).",
      },
      { property: "og:title", content: "Política de Privacidade | H2J Growth Company" },
      {
        property: "og:description",
        content:
          "Transparência sobre a coleta, o uso e a proteção dos seus dados pessoais pela H2J Growth Company, em conformidade com a LGPD.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PrivacyPolicy,
});

function PrivacyPolicy() {
  return (
    <LegalPage title="Política de Privacidade" updated="2 de outubro de 2026">
      <LegalSection title="1. Quem somos">
        <p>
          A <strong>H2J Growth Company</strong> ("H2J", "nós" ou "nossa") é uma consultoria de crescimento que
          conecta estratégia, marketing, aquisição, performance, tecnologia e processo comercial. Este site
          (h2jcompany.com.br) e suas páginas complementares são operados pela H2J.
        </p>
        <p>
          A H2J Growth Company é inscrita no CNPJ sob o nº <strong>57.193.844/0001-13</strong>, com endereço na
          Rua 1001, Balneário Camboriú, estado de Santa Catarina, Brasil.
        </p>
        <p>
          Nesta Política de Privacidade, explicamos de forma clara quais dados pessoais coletamos, por que
          coletamos, como os utilizamos e quais são os seus direitos, em conformidade com a Lei Geral de
          Proteção de Dados Pessoais, Lei nº 13.709/2018 ("LGPD").
        </p>
      </LegalSection>

      <LegalSection title="2. Dados pessoais que coletamos">
        <p>Coletamos dados pessoais quando você preenche um dos nossos formulários (diagnóstico de crescimento, página Construir AI ou cadastro no programa de embaixadores), a saber:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Nome completo;</li>
          <li>Nome da empresa em que atua ou que representa;</li>
          <li>Endereço de e-mail;</li>
          <li>Número de WhatsApp ou telefone;</li>
          <li>Site ou perfil de Instagram da empresa;</li>
          <li>Cidade e estado (programa de embaixadores);</li>
          <li>Empresa e segmento, faturamento mensal aproximado e investimento em mídia (formulário de diagnóstico do site);</li>
          <li>Canais de aquisição de clientes e respostas a perguntas abertas do formulário;</li>
          <li>Modo de participação pretendido e observações livres (programa de embaixadores).</li>
        </ul>
        <p>
          Também coletamos, quando disponíveis, dados técnicos e de navegação associados ao seu contato, como
          a origem do acesso (campanha, anúncio ou link de onde você veio, parâmetros UTM e página de
          referência), além de dados básicos de acesso (navegador, dispositivo e endereço IP), utilizados para
          medir o desempenho das nossas campanhas e melhorar o site.
        </p>
        <p>Não solicitamos, por meio deste site, dados sensíveis (origem racial ou étnica, convicção religiosa, opinião política, saúde, biometria e afins), dados financeiros pessoais ou documentos de identificação. Pedimos que você não envie esse tipo de informação pelos formulários.</p>
      </LegalSection>

      <LegalSection title="3. Para que utilizamos seus dados">
        <p>Utilizamos os dados pessoais coletados exclusivamente para:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Entrar em contato com você pelo e-mail, telefone ou WhatsApp informado, para entender o seu cenário e avaliar se o nosso diagnóstico ou serviço faz sentido para a sua empresa;</li>
          <li>Elaborar análises, diagnósticos, propostas e apresentações comerciais personalizadas;</li>
          <li>Gerenciar o relacionamento comercial com você e com a sua empresa, inclusive o acompanhamento pós-contato;</li>
          <li>Avaliar e gerenciar candidaturas ao programa de embaixadores e a parcerias;</li>
          <li>Produzir estatísticas agregadas sobre o desempenho das nossas campanhas e páginas, para melhoria contínua do site e dos serviços;</li>
          <li>Cumprir obrigações legais e regulatórias aplicáveis.</li>
        </ul>
        <p>Não utilizamos seus dados para decisões automatizadas que produzam efeitos jurídicos ou que impactem significativamente seus interesses, e não vendemos, alugamos ou negociamos seus dados pessoais com terceiros.</p>
      </LegalSection>

      <LegalSection title="4. Bases legais do tratamento">
        <p>Tratamos seus dados pessoais com base nas seguintes hipóteses legais da LGPD:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li><strong>Consentimento (art. 7º, I):</strong> quando você preenche e envia um formulário, você nos autoriza expressamente a utilizar os dados informados para as finalidades descritas nesta Política;</li>
          <li><strong>Execução de contrato e procedimentos pré-contratuais (art. 7º, V):</strong> quando o tratamento é necessário para elaborar propostas, prestar serviços e gerenciar a relação comercial;</li>
          <li><strong>Legítimo interesse (art. 7º, IX):</strong> para o relacionamento comercial com empresas, a organização interna dos contatos, a segurança do site e a melhoria das nossas operações, sempre dentro do que você razoavelmente espera do nosso contato;</li>
          <li><strong>Cumprimento de obrigação legal (art. 7º, II):</strong> quando necessário para atender exigências legais, fiscais ou de autoridades.</li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Compartilhamento de dados">
        <p>
          Seus dados pessoais podem ser compartilhados apenas com prestadores de serviços que nos apoiam na
          operação deste site e do nosso negócio, como provedores de hospedagem e infraestrutura em nuvem,
          serviços de e-mail, planilhas Google para organização dos contatos, ferramentas de gestão de contatos e relacionamento com clientes (CRM) e
          ferramentas de análise de tráfego, sempre limitados ao necessário para cada finalidade e protegidos
          por obrigações contratuais de confidencialidade e segurança.
        </p>
        <p>
          Alguns desses provedores podem estar localizados fora do Brasil. Nesses casos, adotamos as garantias
          exigidas pela LGPD para transferência internacional de dados.
        </p>
        <p>
          Poderemos, ainda, compartilhar dados quando houver determinação legal, ordem judicial ou requisição
          de autoridade pública competente, e em operações de reorganização societária da H2J, nas mesmas
          condições desta Política.
        </p>
      </LegalSection>

      <LegalSection title="6. Prazo de retenção">
        <p>
          Mantemos seus dados pessoais pelo tempo necessário para cumprir as finalidades descritas nesta
          Política, em geral, durante o relacionamento comercial com você ou sua empresa e, depois disso,
          pelo prazo razoável para eventual follow-up, defesa em processos e cumprimento de obrigações legais.
          Quando os dados deixarem de ser necessários, serão excluídos ou anonimizados de forma segura.
        </p>
        <p>Você pode solicitar a eliminação dos seus dados a qualquer momento, conforme descrito no item 8, ressalvadas as hipóteses de retenção permitidas por lei.</p>
      </LegalSection>

      <LegalSection title="7. Segurança dos dados">
        <p>
          Adotamos medidas técnicas e administrativas adequadas para proteger seus dados pessoais contra
          acesso não autorizado, perda, alteração e divulgação indevidas, incluindo criptografia em trânsito,
          controle de acesso restrito às pessoas que precisam dos dados para desempenhar suas funções e
          monitoramento da infraestrutura. Nenhum sistema é totalmente imune, mas nos esforçamos para
          proteger seus dados com o cuidado que a LGPD exige.
        </p>
      </LegalSection>

      <LegalSection title="8. Seus direitos">
        <p>A LGPD garante a você, como titular de dados pessoais, os seguintes direitos, que a H2J respeita integralmente:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Confirmar a existência de tratamento dos seus dados;</li>
          <li>Acessar os dados que mantemos sobre você;</li>
          <li>Corrigir dados incompletos, inexatos ou desatualizados;</li>
          <li>Solicitar a anonimização, o bloqueio ou a eliminação de dados desnecessários, excessivos ou tratados em desconformidade com a lei;</li>
          <li>Solicitar a portabilidade dos seus dados a outro fornecedor de serviço;</li>
          <li>Eliminar os dados tratados com base no consentimento;</li>
          <li>Receber informações sobre com quem compartilhamos seus dados;</li>
          <li>Receber informações sobre as consequências de não fornecer seus dados;</li>
          <li>Revogar o consentimento a qualquer momento.</li>
        </ul>
        <p>
          Para exercer qualquer um desses direitos, basta escrever para{" "}
          <a href="mailto:contato@h2jcompany.com.br">contato@h2jcompany.com.br</a>. Responderemos ao seu
          pedido no prazo legal. Se você entende que seus direitos foram violados, também pode reclamar à
          Autoridade Nacional de Proteção de Dados (ANPD).
        </p>
      </LegalSection>

      <LegalSection title="9. Cookies e navegação">
        <p>
          Utilizamos recursos essenciais de navegação e, quando ativadas, ferramentas de medição de tráfego e
          desempenho de campanhas, que podem registrar informações sobre sua visita (páginas acessadas, origem
          do acesso e interações com o site) de forma agregada. Você pode gerenciar ou limpar cookies nas
          configurações do seu navegador; o bloqueio de cookies essenciais pode afetar o funcionamento de
          algumas funcionalidades do site.
        </p>
      </LegalSection>

      <LegalSection title="10. Menores de idade">
        <p>
          Este site é destinado a empresas e profissionais. Não coletamos intencionalmente dados de menores de
          18 anos. Se você entende que dados de um menor foram enviados por meio dos nossos formulários,
          entre em contato para que removamos as informações.
        </p>
      </LegalSection>

      <LegalSection title="11. Alterações desta Política">
        <p>
          Esta Política pode ser atualizada para refletir mudanças nos nossos processos, nos serviços ou na
          legislação. A data da "última atualização" no topo da página indica a versão vigente. Alterações
          relevantes no tratamento de dados já coletados serão comunicadas pelos canais de contato que você
          nos forneceu.
        </p>
      </LegalSection>

      <LegalSection title="12. Contato">
        <p>
          Encarregado de dados (DPO): canal de atendimento da H2J Growth Company para assuntos de proteção de
          dados.
        </p>
        <p>
          E-mail: <a href="mailto:contato@h2jcompany.com.br">contato@h2jcompany.com.br</a> · WhatsApp: +55 (11)
          97148-8408 · Instagram: @h2jcompany
        </p>
      </LegalSection>
    </LegalPage>
  );
}
